import { Link } from "react-router-dom";
import { LegalShell, Section, UPDATED } from "@/pages/Legal";

const A = ({ to, children }) => <Link to={to} className="text-[#B8A4FF] hover:underline">{children}</Link>;

export default function Terms() {
  return (
    <LegalShell eyebrow="LEGAL" title="Terms of service" updated={UPDATED} testid="terms-page"
      seo={{ title: "Terms of Service", description: "The terms governing use of Studivault, operated by Bloc Metric, India — accounts, plans, acceptable use, AI content, liability and governing law.", path: "/terms" }}>
      <p>
        These Terms of Service ("Terms") form a binding agreement between you and <strong>Bloc Metric</strong>,
        India ("we", "us"), which operates Studivault at <strong>studivault.com</strong> (the "Service"). This
        document is an electronic record under the Information Technology Act, 2000 and does not require
        physical or digital signatures. By creating an account or using the Service you agree to these Terms,
        our <A to="/privacy-policy">Privacy policy</A>, <A to="/refund-policy">Refund policy</A> and{" "}
        <A to="/cookie-policy">Cookie policy</A>. If you do not agree, do not use the Service.
      </p>

      <Section n={1} title="The Service">
        Studivault is an AI-assisted study platform that turns your notes, documents, recordings and links
        into study notes, flashcards, quizzes, mock exams and tutor explanations, with gamification
        (XP, streaks, gems, leaderboards), study rooms and a marketplace for sharing study packs.
      </Section>

      <Section n={2} title="Eligibility">
        You must be at least 18 years old and competent to contract under the Indian Contract Act, 1872. If
        you are under 18, you may use the Service only with the consent and supervision of a parent or lawful
        guardian, who agrees to these Terms on your behalf and is responsible for your use, including any
        purchases. We may suspend accounts where we reasonably believe this requirement is not met.
      </Section>

      <Section n={3} title="Your account">
        You must provide accurate information, keep your credentials confidential and are responsible for all
        activity under your account. Notify us immediately of any unauthorised use. We recommend enabling
        two-factor authentication.
      </Section>

      <Section n={4} title="Plans, pricing and payments">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>The Service offers a Free plan and paid Plus and Pro plans. Prices are shown on the billing page in INR (inclusive of applicable taxes unless stated) or USD.</li>
          <li>Paid plans are <strong>one-time purchases of a 30-day pass</strong>. They do <strong>not</strong> auto-renew and you will not be charged again unless you choose to buy another pass.</li>
          <li>Payments are processed by Razorpay; their terms also apply to your payment.</li>
          <li>We may change prices or features with prior notice; changes do not affect passes already purchased.</li>
          <li>Refunds are governed by our <A to="/refund-policy">Refund policy</A>.</li>
          <li>Gems and XP are virtual, in-app rewards with no cash value; they cannot be sold, transferred or exchanged for money.</li>
        </ul>
      </Section>

      <Section n={5} title="Acceptable use">
        <p>You agree not to host, upload, share or generate content, or use the Service in a way, that:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>belongs to another person without the right to use it, or infringes any patent, trademark, copyright or other proprietary right;</li>
          <li>is obscene, pornographic, paedophilic, invasive of privacy, harassing, defamatory, hateful, racially or ethnically objectionable, or harmful to children;</li>
          <li>impersonates another person, deceives or misleads, or knowingly spreads misinformation;</li>
          <li>threatens the unity, integrity, defence, security or sovereignty of India, friendly relations with foreign states or public order;</li>
          <li>contains malware or attempts to gain unauthorised access, scrape, overload, reverse-engineer or disrupt the Service;</li>
          <li>circumvents plan limits, abuses referral/gem systems, or uses automated means to create accounts;</li>
          <li>is used for academic dishonesty prohibited by your institution, or violates any applicable law.</li>
        </ul>
        <p>We may remove content and suspend or terminate accounts that breach this section, and report unlawful activity to authorities.</p>
      </Section>

      <Section n={6} title="Your content and licence">
        You retain ownership of the content you upload. You grant us a worldwide, non-exclusive, royalty-free
        licence to host, process, transmit (including to our AI processors) and display it solely to operate
        and improve the Service for you. When you publish a pack publicly, you also permit other users to view
        and use it within the Service. You represent that you have all rights needed to upload your content.
      </Section>

      <Section n={7} title="AI-generated output">
        Study material is generated by AI and may be inaccurate, incomplete or outdated. It is provided for
        learning assistance only and is not professional, medical, legal or financial advice. Verify important
        facts with authoritative sources. See our <A to="/disclaimer">Disclaimer</A>.
      </Section>

      <Section n={8} title="Intellectual property">
        The Service, including its software, design, logos, the Studivault name and all content created by us,
        is owned by Bloc Metric and protected by intellectual property laws. You receive a limited, revocable,
        non-transferable licence to use the Service for personal, non-commercial study. You may not copy,
        modify, distribute or create derivative works of the Service except as permitted by these Terms.
      </Section>

      <Section n={9} title="Copyright and takedown">
        If you believe content on the Service infringes your rights, contact our Grievance Officer with details
        of the work, the infringing material's location and your contact information. We will act on valid
        notices within the timelines prescribed by the IT Rules, 2021.
      </Section>

      <Section n={10} title="Demo accounts and sample content">
        To help new users understand how the Service works, leaderboards, the marketplace and other areas may
        display <em>demo</em> or <em>sample</em> accounts and example study packs created by us for demonstration
        purposes. These do not represent real individual users and do not reflect endorsements or real activity.
      </Section>

      <Section n={11} title="Third-party services">
        The Service integrates third-party services (such as Google, Razorpay, Cloudflare, DeepSeek and YouTube).
        We are not responsible for their availability or practices, which are governed by their own terms.
      </Section>

      <Section n={12} title="Termination">
        You may stop using the Service and delete your account at any time. We may suspend or terminate your
        access, with or without notice, if you breach these Terms, if required by law, or to protect the
        Service or other users. On termination your right to use the Service ends; sections that by their
        nature should survive (including IP, disclaimers, liability, indemnity and governing law) survive.
      </Section>

      <Section n={13} title="Disclaimer of warranties">
        The Service is provided "as is" and "as available" without warranties of any kind, express or implied,
        including merchantability, fitness for a particular purpose, accuracy or uninterrupted availability,
        to the maximum extent permitted by law.
      </Section>

      <Section n={14} title="Limitation of liability">
        To the maximum extent permitted by law, Bloc Metric and its partners, employees and affiliates will not
        be liable for any indirect, incidental, special, consequential or punitive damages, or loss of data,
        profits, goodwill or academic results, arising from your use of the Service. Our total aggregate
        liability for any claim will not exceed the amount you paid us in the three (3) months before the
        event giving rise to the claim, or INR 1,000, whichever is higher. Nothing limits liability that cannot
        be limited under applicable law, including your rights under the Consumer Protection Act, 2019.
      </Section>

      <Section n={15} title="Indemnity">
        You agree to indemnify and hold harmless Bloc Metric and its partners, employees and affiliates from
        any claims, losses, liabilities and expenses (including reasonable legal fees) arising from your
        content, your breach of these Terms or your violation of any law or third-party right.
      </Section>

      <Section n={16} title="Governing law and disputes">
        These Terms are governed by the laws of India. Subject to any mandatory consumer protection rights, the
        courts at <strong>Mumbai, Maharashtra</strong> have exclusive jurisdiction. Before filing a claim,
        please contact our <A to="/grievance">Grievance Officer</A> so we can try to resolve the issue.
      </Section>

      <Section n={17} title="Changes to these Terms">
        We may update these Terms from time to time. The "Last updated" date shows the current version; we will
        notify you of material changes in the app or by email. Continued use after changes take effect
        constitutes acceptance.
      </Section>

      <Section n={18} title="Contact">
        Bloc Metric, India · <a href="mailto:shaurya@sanghviinfo.com" className="text-[#B8A4FF] hover:underline">shaurya@sanghviinfo.com</a> ·{" "}
        <A to="/contact">Contact form</A>
      </Section>
    </LegalShell>
  );
}
